import type { Me } from '@masar/api-client';
import { createContext, use, useCallback, type ReactNode } from 'react';
import { hasAny, hasPerm, type Perm } from './permissions';

const MeCtx = createContext<Me | null>(null);

export function MeProvider({ me, children }: { me: Me; children: ReactNode }) {
  return <MeCtx value={me}>{children}</MeCtx>;
}

/** The signed-in member (only inside the signed-in shell). */
export function useMeStrict(): Me {
  const me = use(MeCtx);
  if (!me) throw new Error('useMeStrict outside the signed-in shell');
  return me;
}

/** can('f.booking.cancel') — builds buttons and navigation from permissions; the server enforces the same rules. */
export function useCan(): (perm: Perm) => boolean {
  const me = use(MeCtx);
  return useCallback((perm: Perm) => hasPerm(me?.permissions, perm), [me]);
}

export function useCanAny(): (perms: readonly Perm[]) => boolean {
  const me = use(MeCtx);
  return useCallback((perms: readonly Perm[]) => hasAny(me?.permissions, perms), [me]);
}

/** Renders children only when the member holds the permission. */
export function Can({
  perm,
  children,
  fallback = null,
}: {
  perm: Perm;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const can = useCan();
  return <>{can(perm) ? children : fallback}</>;
}
